import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Copy, Link2, Mail, UserPlus, KeyRound, CheckCircle2 } from 'lucide-react';
import { ACQUISITION_OPTIONS, type AcquisitionSource } from '@/lib/onboarding';
import { cn } from '@/lib/utils';

type AccessMethod = 'invite' | 'magiclink' | 'password';

interface CreateResult {
  method: AccessMethod;
  message: string;
  actionLink?: string | null;
  temporaryPassword?: string;
  userId?: string | null;
}

interface AdminCreateUserDialogProps {
  onCreated?: () => void;
}

export function AdminCreateUserDialog({ onCreated }: AdminCreateUserDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [accessMethod, setAccessMethod] = useState<AccessMethod>('invite');
  const [password, setPassword] = useState('');
  const [acquisitionSource, setAcquisitionSource] = useState<AcquisitionSource | 'none'>('none');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CreateResult | null>(null);

  const reset = () => {
    setName('');
    setEmail('');
    setAccessMethod('invite');
    setPassword('');
    setAcquisitionSource('none');
    setResult(null);
  };

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error('Nombre y correo son obligatorios');
      return;
    }
    if (accessMethod === 'password' && password.length < 8) {
      toast.error('La contraseña temporal debe tener al menos 8 caracteres');
      return;
    }

    setLoading(true);
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        accessMethod,
      };
      if (accessMethod === 'password') body.password = password;
      if (acquisitionSource !== 'none') body.acquisitionSource = acquisitionSource;

      const { data, error } = await supabase.functions.invoke('create-admin-user', { body });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setResult({
        method: data.method,
        message: data.message,
        actionLink: data.actionLink,
        temporaryPassword: data.temporaryPassword,
        userId: data.userId,
      });
      toast.success(data.message ?? 'Usuario creado');
      onCreated?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al crear usuario');
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado al portapapeles`);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button className="gap-2">
          <UserPlus className="h-4 w-4" />
          Crear usuario
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-lg gap-0 p-0 overflow-hidden">
        <div className="bg-gradient-to-br from-primary/10 via-card to-accent/5 px-6 pt-6 pb-4 border-b">
          <DialogHeader className="text-left space-y-1">
            <DialogTitle className="text-xl">Nuevo cliente</DialogTitle>
            <DialogDescription>
              Crea una cuenta y elige cómo recibirá acceso a FOCO.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {!result ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="create-name">Nombre completo</Label>
                  <Input
                    id="create-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="María López"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="create-email">Correo electrónico</Label>
                  <Input
                    id="create-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="cliente@correo.com"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>¿Cómo llegó a FOCO? (opcional)</Label>
                  <Select
                    value={acquisitionSource}
                    onValueChange={(v) => setAcquisitionSource(v as AcquisitionSource | 'none')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sin especificar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin especificar</SelectItem>
                      {ACQUISITION_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.emoji} {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Método de acceso</Label>
                <RadioGroup
                  value={accessMethod}
                  onValueChange={(v) => setAccessMethod(v as AccessMethod)}
                  className="grid gap-2"
                >
                  {[
                    {
                      value: 'invite' as const,
                      icon: Mail,
                      title: 'Invitación por correo',
                      desc: 'Stripe/Supabase envía un email para establecer contraseña',
                    },
                    {
                      value: 'magiclink' as const,
                      icon: Link2,
                      title: 'Enlace de acceso',
                      desc: 'Genera un link para copiar y compartir (WhatsApp, etc.)',
                    },
                    {
                      value: 'password' as const,
                      icon: KeyRound,
                      title: 'Contraseña temporal',
                      desc: 'Tú defines la clave; compártela de forma segura',
                    },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={cn(
                        'flex cursor-pointer gap-3 rounded-xl border p-3 transition-colors',
                        accessMethod === opt.value
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'hover:bg-muted/50',
                      )}
                    >
                      <RadioGroupItem value={opt.value} className="mt-1" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 font-medium text-sm">
                          <opt.icon className="h-4 w-4 shrink-0 text-primary" />
                          {opt.title}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              {accessMethod === 'password' && (
                <div className="space-y-2">
                  <Label htmlFor="create-password">Contraseña temporal</Label>
                  <Input
                    id="create-password"
                    type="text"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mín. 8 caracteres, mayúscula, minúscula y número"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4 animate-fade-in">
              <div className="flex items-start gap-3 rounded-xl border border-success/30 bg-success/5 p-4">
                <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Usuario listo</p>
                  <p className="text-sm text-muted-foreground mt-1">{result.message}</p>
                </div>
              </div>

              {result.temporaryPassword && (
                <div className="rounded-xl border bg-muted/30 p-4 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Contraseña temporal (solo se muestra una vez)
                  </p>
                  <div className="flex gap-2">
                    <Input readOnly value={result.temporaryPassword} className="font-mono text-sm" />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => copyText(result.temporaryPassword!, 'Contraseña')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {result.actionLink && (
                <div className="rounded-xl border bg-muted/30 p-4 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Enlace de acceso
                  </p>
                  <div className="flex gap-2">
                    <Input readOnly value={result.actionLink} className="text-xs" />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => copyText(result.actionLink!, 'Enlace')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20 gap-2 sm:gap-2">
          {!result ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} disabled={loading} className="gap-2">
                {loading ? 'Creando…' : 'Crear usuario'}
              </Button>
            </>
          ) : (
            <Button
              className="w-full"
              onClick={() => {
                reset();
                setOpen(false);
              }}
            >
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
